/**
 * udob-facets.js
 *
 * Search & Discovery API を使ったサーバーサイドフィルタリング。
 * Shopify セクションレンダリング API（?sections=<section-id>）で
 * 商品グリッド・ページネーション・アクティブバッジを非同期更新する。
 *
 * フリーワード検索は全ページをAJAXで取得してクライアントサイドで絞り込む。
 * カテゴリ・在庫・ソートはサーバーサイドで処理。
 *
 * ─── URLパラメータ構造 ───────────────────────────────────────────────────
 *  フリーワード:             q=キーワード（URLに記録のみ、サーバー未処理）
 *  商品カテゴリ（OR複数）: filter.p.m.custom.product_category=#置物&...
 *  UDOBカテゴリ（単一）:   filter.p.m.custom.udob_category=#古物
 *  在庫あり:               filter.v.availability=1
 *  並び順:                 sort_by=created-descending | created-ascending
 *  ページ:                 page=2
 * ────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* スクロール復元を手動管理（pushState 後にブラウザが上書きするのを防ぐ） */
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  /* ─── 対象要素（定数より先に取得） ───────────────────────────────── */
  var section = document.querySelector('[data-section-id]');
  var grid    = document.getElementById('udob-grid');

  if (!section || !grid) return;

  /* ─── 定数 ─────────────────────────────────────────────────────────── */
  var SECTION_ID     = section.getAttribute('data-filter-section-id') || section.getAttribute('data-section-id');
  var _collectionUrl = section.getAttribute('data-collection-url');
  var COLLECTION_URL = (_collectionUrl && _collectionUrl !== '') ? _collectionUrl : window.location.pathname;
  var PARAM_Q     = 'q';
  var PARAM_PROD  = 'filter.p.m.custom.product_category';
  var PARAM_UDOB  = 'filter.p.m.custom.udob_category';
  var PARAM_AVAIL = 'filter.v.availability';
  var PARAM_SORT  = 'sort_by';

  /* ─── その他の対象要素 ─────────────────────────────────────────────── */
  var availableCheck = document.getElementById('udob-available-only');
  var availableLabel = document.getElementById('udob-available-label');
  var sortToggle     = document.querySelector('.udob-items__sort-toggle');
  var searchInput    = document.getElementById('udob-search-input');
  var loader         = document.getElementById('udob-loader');

  /* ─── スクロールフェードイン ────────────────────────────────────────── */
  var _fadeObserver = null;

  function initFadeObserver() {
    if (!window.IntersectionObserver) return;
    if (_fadeObserver) _fadeObserver.disconnect();

    _fadeObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          _fadeObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -20px 0px' });

    var cols  = getColumnCount();
    var items = grid.querySelectorAll('.udob-items__card, .udob-items__showcase');
    items.forEach(function (el, idx) {
      var col = idx % cols;
      el.style.transitionDelay = (col * 80) + 'ms';
      _fadeObserver.observe(el);
    });
  }

  /* ─── ローダー制御 ─────────────────────────────────────────────────── */
  function showLoader() {
    if (loader) { loader.classList.add('is-visible'); loader.removeAttribute('aria-hidden'); }
    if (grid)   { grid.classList.add('is-loading'); }
  }

  function hideLoader() {
    if (loader) { loader.classList.remove('is-visible'); loader.setAttribute('aria-hidden', 'true'); }
    if (grid)   { grid.classList.remove('is-loading'); }
  }

  /* ─── ユーティリティ ────────────────────────────────────────────────── */

  function getColumnCount() {
    if (window.innerWidth <= 768)  return 2;
    if (window.innerWidth <= 999) return 3;
    if (window.innerWidth >= 1200) return 5;
    return 4;
  }

  function equalizeCardTags() {
    if (!grid) return;
    var cols = getColumnCount();

    // Reset inline styles
    grid.querySelectorAll('.udob-items__card-tags').forEach(function (el) {
      el.style.minHeight = '';
    });
    grid.querySelectorAll('.udob-items__card-image-filter').forEach(function (el) {
      el.style.height = '';
    });

    // Assign each card to a row by walking grid children in DOM order
    var rowMap = {};
    var colIdx = 0;
    var rowIdx = 0;
    Array.from(grid.children).forEach(function (child) {
      if (colIdx >= cols) { colIdx = 0; rowIdx++; }
      if (child.classList.contains('udob-items__card')) {
        if (!rowMap[rowIdx]) rowMap[rowIdx] = [];
        rowMap[rowIdx].push(child);
      }
      colIdx++;
    });

    Object.keys(rowMap).forEach(function (key) {
      var row = rowMap[key];
      if (row.length < 2) return;

      // タグ高さを揃える（段数が違う場合）
      var tagHeights = row.map(function (card) {
        var tags = card.querySelector('.udob-items__card-tags');
        return tags ? tags.offsetHeight : 0;
      });
      var maxTagH = Math.max.apply(null, tagHeights);
      var minTagH = Math.min.apply(null, tagHeights);
      if (maxTagH !== minTagH) {
        row.forEach(function (card) {
          var tags = card.querySelector('.udob-items__card-tags');
          if (tags) tags.style.minHeight = maxTagH + 'px';
        });
      }

      // 画像コンテナの高さを揃える（列幅サブピクセル差を吸収）
      // getBoundingClientRect で小数値を取得し、offsetHeight では検出できない差も均一化
      var imgRects = row.map(function (card) {
        var img = card.querySelector('.udob-items__card-image-filter');
        return img ? img.getBoundingClientRect().height : 0;
      });
      var maxImgH = Math.max.apply(null, imgRects);
      var minImgH = Math.min.apply(null, imgRects);
      if (maxImgH - minImgH > 0.01) {
        var targetH = Math.floor(minImgH);
        row.forEach(function (card) {
          var img = card.querySelector('.udob-items__card-image-filter');
          if (img) img.style.height = targetH + 'px';
        });
      }
    });
  }

  function updateBorderRight() {
    var cols     = getColumnCount();
    var allItems = Array.from(grid.querySelectorAll('.udob-items__showcase, .udob-items__card'));
    allItems.forEach(function (el) { el.classList.remove('is-last-col'); });
    var visible  = allItems.filter(function (el) { return el.style.display !== 'none'; });
    visible.forEach(function (el, idx) {
      if ((idx + 1) % cols === 0) el.classList.add('is-last-col');
    });
  }

  function updateShowcaseVisibility(hasQuery) {
    grid.querySelectorAll('.udob-items__showcase').forEach(function (sc) {
      sc.style.display = hasQuery ? 'none' : '';
    });
  }

  /* ─── URL構築 ───────────────────────────────────────────────────────── */

  /** フリーワード検索URL（q パラメータのみ変更） */
  function buildSearchUrl(query) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    if (query) { params.set(PARAM_Q, query); } else { params.delete(PARAM_Q); }
    return buildUrl(params);
  }

  /** 商品カテゴリトグル（複数選択 / OR）。検索はクリア。 */
  function buildProductCatUrl(value) {
    var params   = new URLSearchParams(window.location.search);
    params.delete(PARAM_Q);
    var existing = params.getAll(PARAM_PROD);
    params.delete(PARAM_PROD);
    if (existing.indexOf(value) !== -1) {
      existing.filter(function (v) { return v !== value; })
              .forEach(function (v) { params.append(PARAM_PROD, v); });
    } else {
      existing.forEach(function (v) { params.append(PARAM_PROD, v); });
      params.append(PARAM_PROD, value);
    }
    params.delete('page');
    return buildUrl(params);
  }

  /** UDOBカテゴリトグル（単一選択）。検索はクリア。 */
  function buildUdoBCatUrl(value) {
    var params  = new URLSearchParams(window.location.search);
    params.delete(PARAM_Q);
    var current = params.get(PARAM_UDOB);
    if (current === value) { params.delete(PARAM_UDOB); } else { params.set(PARAM_UDOB, value); }
    params.delete('page');
    return buildUrl(params);
  }

  /** 在庫ありフィルタートグル。検索はクリア。 */
  function buildAvailableUrl(enabled) {
    var params = new URLSearchParams(window.location.search);
    params.delete(PARAM_Q);
    if (enabled) { params.set(PARAM_AVAIL, '1'); } else { params.delete(PARAM_AVAIL); }
    params.delete('page');
    return buildUrl(params);
  }

  /** 並び順URL。検索はクリア。 */
  function buildSortUrl(sortValue) {
    var params = new URLSearchParams(window.location.search);
    params.delete(PARAM_Q);
    params.set(PARAM_SORT, sortValue);
    params.delete('page');
    return buildUrl(params);
  }

  function buildUrl(params) {
    var qs = params.toString();
    return COLLECTION_URL + (qs ? '?' + qs : '');
  }

  /* ─── 全ページ取得（フリーワード検索用） ────────────────────────────── */

  /**
   * 現在のフィルター状態でコレクション全ページを順にAJAX取得し、
   * 全商品カード要素の配列を返す。
   * @param {URLSearchParams} filterParams  q・page を除いたフィルターパラメータ
   * @returns {Promise<Element[]>}
   */
  function fetchAllCards(filterParams) {
    var cards = [];

    function fetchPage(pageNum) {
      var params = new URLSearchParams(filterParams.toString());
      if (pageNum > 1) params.set('page', String(pageNum));
      var qs      = params.toString();
      var url     = COLLECTION_URL + (qs ? '?' + qs + '&' : '?') + 'sections=' + SECTION_ID;

      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var html = data[SECTION_ID];
          if (!html) return;
          var parser    = new DOMParser();
          var doc       = parser.parseFromString(html, 'text/html');
          var pageCards = Array.from(doc.querySelectorAll('.udob-items__card'));
          if (pageCards.length === 0) return; /* これ以上ページなし */
          pageCards.forEach(function (c) { cards.push(c); });
          return fetchPage(pageNum + 1);
        });
    }

    return fetchPage(1).then(function () { return cards; });
  }

  /* ─── クライアントサイド全件検索 ────────────────────────────────────── */

  var _searchKey = 0; /* 古い非同期処理の結果を破棄するためのキー */

  /**
   * フリーワード検索を実行する。
   * query が空の場合は通常の paginate ビューに戻る。
   * @param {string} query
   */
  function applyClientSearch(query) {
    if (!query) {
      /* 検索クリア → 通常ページビューに戻る */
      var params = new URLSearchParams(window.location.search);
      params.delete(PARAM_Q);
      params.delete('page');
      navigateTo(buildUrl(params));
      return;
    }

    var myKey       = ++_searchKey;
    var filterParams = new URLSearchParams(window.location.search);
    filterParams.delete(PARAM_Q);
    filterParams.delete('page');

    showLoader();

    fetchAllCards(filterParams)
      .then(function (allCards) {
        if (myKey !== _searchKey) return; /* 別の検索が走ったので破棄 */

        var q        = query.toLowerCase();
        var matching = allCards.filter(function (card) {
          var title = card.getAttribute('data-title') || '';
          var tags  = card.getAttribute('data-tags')  || '';
          return title.includes(q) || tags.includes(q);
        });

        /* グリッドを検索結果で置換 */
        grid.innerHTML = '';
        if (matching.length === 0) {
          var empty = document.createElement('div');
          empty.className = 'udob-items__empty';
          empty.textContent = '商品が見つかりませんでした。';
          grid.appendChild(empty);
        } else {
          matching.forEach(function (card) { grid.appendChild(card); });
        }

        /* ページネーションを非表示 */
        var paginationEl = document.getElementById('udob-pagination');
        if (paginationEl) paginationEl.style.display = 'none';

        hideLoader();
        updateShowcaseVisibility(true);
        updateBorderRight();
        initFadeObserver();

        /* URL を更新（pushState のみ、fetch なし） */
        var newUrl = buildSearchUrl(query);
        history.pushState({ url: newUrl }, '', newUrl);
      })
      .catch(function (err) {
        hideLoader();
        console.warn('[udob-facets] search error:', err);
      });
  }

  /* ─── AJAX ──────────────────────────────────────────────────────────── */

  function navigateTo(url, pushState, noScroll) {
    if (pushState === undefined) pushState = true;
    ++_searchKey; /* 進行中の検索を無効化 */
    showLoader();

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var newDoc = parser.parseFromString(html, 'text/html');

        /* フォーカス中要素の DOM 削除でブラウザが最上部へスクロールするのを防ぐ */
        if (document.activeElement && document.activeElement !== document.body) {
          document.activeElement.blur();
        }

        swapInner(newDoc, '#udob-grid');
        swapOuter(newDoc, '#udob-pagination');
        swapOuter(newDoc, '#udob-count');

        syncButtonStatesFromUrl(new URLSearchParams(url.split('?')[1] || ''));

        hideLoader();
        updateBorderRight();
        initFadeObserver();
        setTimeout(equalizeCardTags, 50);

        if (pushState) history.pushState({ url: url }, '', url);
        else history.replaceState({ url: url }, '', url);

        if (!noScroll) {
          requestAnimationFrame(function () {
            var filterOutputEl = document.getElementById('udob-filter-output');
            var filterVisible = filterOutputEl && filterOutputEl.style.display !== 'none';
            var countEl = document.getElementById('udob-count');
            var scrollTarget = (filterVisible ? filterOutputEl : countEl) || grid;
            var headerEl = document.getElementById('shopify-section-group-header-group');
            var headerH = headerEl ? headerEl.offsetHeight : 0;
            var extraOffset = filterVisible ? 72 : 80;
            var top = scrollTarget.getBoundingClientRect().top + window.scrollY - headerH - extraOffset;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          });
        }
      })
      .catch(function (err) {
        hideLoader();
        console.warn('[udob-facets] フォールバック遷移:', err);
        window.location.href = url;
      });
  }

  function swapInner(sourceDoc, selector) {
    var target = document.querySelector(selector);
    var source = sourceDoc.querySelector(selector);
    if (target && source) target.innerHTML = source.innerHTML;
  }

  function swapOuter(sourceDoc, selector) {
    var target = document.querySelector(selector);
    var source = sourceDoc.querySelector(selector);
    if (target && source) {
      target.outerHTML = source.outerHTML;
    } else if (!source && target) {
      target.style.display = 'none';
    }
  }

  /* ─── フィルター出力バッジ描画（URLパラメータ基準） ─────────────────── */

  var CLOSE_SVG = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.4 15L10 11.4L13.6 15L15 13.6L11.4 10L15 6.4L13.6 5L10 8.6L6.4 5L5 6.4L8.6 10L5 13.6L6.4 15ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z" fill="#F7F1E1"/></svg>';

  function renderFilterOutput(params) {
    var filterOutput = document.getElementById('udob-filter-output');
    if (!filterOutput) return;

    var activeProdCats = params.getAll(PARAM_PROD).map(function (v) { return decodeURIComponent(v); });
    var activeUdobCat  = decodeURIComponent(params.get(PARAM_UDOB) || '');
    var availActive    = params.get(PARAM_AVAIL) === '1';
    var hasActive      = activeProdCats.length > 0 || activeUdobCat !== '' || availActive;

    filterOutput.style.display = hasActive ? '' : 'none';
    if (!hasActive) return;

    /* remove URL をパラメータから直接構築 */
    function makeRemoveUrl(key, val) {
      var p = new URLSearchParams(params.toString());
      if (val !== undefined) {
        var rest = p.getAll(key).filter(function (v) { return decodeURIComponent(v) !== val; });
        p.delete(key);
        rest.forEach(function (v) { p.append(key, v); });
      } else {
        p.delete(key);
      }
      p.delete('page');
      return buildUrl(p);
    }

    function makeTag(label, url) {
      var a = document.createElement('a');
      a.href = url;
      a.className = 'udob-items__active-tag';
      a.setAttribute('data-facet-link', '');
      a.innerHTML = label + CLOSE_SVG;
      return a;
    }

    var tagsContainer = filterOutput.querySelector('#udob-filter-tags');
    if (!tagsContainer) return;
    tagsContainer.innerHTML = '';

    activeProdCats.forEach(function (val) {
      tagsContainer.appendChild(makeTag(val, makeRemoveUrl(PARAM_PROD, val)));
    });
    if (activeUdobCat) {
      tagsContainer.appendChild(makeTag(activeUdobCat, makeRemoveUrl(PARAM_UDOB)));
    }
    if (availActive) {
      tagsContainer.appendChild(makeTag('販売中のみ', makeRemoveUrl(PARAM_AVAIL)));
    }
  }

  /* ─── ボタン・入力欄アクティブ状態同期 ──────────────────────────────── */

  function syncButtonStatesFromUrl(params) {
    var activeProdCats = params.getAll(PARAM_PROD);
    var activeUdobCat  = params.get(PARAM_UDOB) || '';
    var sortValue      = params.get(PARAM_SORT)  || 'created-descending';
    var availActive    = params.get(PARAM_AVAIL) === '1';
    var searchQuery    = params.get(PARAM_Q) || '';

    document.querySelectorAll('[data-filter-group="product"]').forEach(function (btn) {
      var val = decodeURIComponent(btn.getAttribute('data-filter-value') || '');
      btn.classList.toggle('is-active', activeProdCats.indexOf(val) !== -1);
    });

    document.querySelectorAll('[data-filter-group="udob"]').forEach(function (btn) {
      var val = decodeURIComponent(btn.getAttribute('data-filter-value') || '');
      btn.classList.toggle('is-active', val === activeUdobCat);
    });

    var sortIsAsc = (sortValue === 'created-ascending');
    document.querySelectorAll('.udob-items__sort-btn').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-sort') === sortValue);
    });
    if (sortToggle) sortToggle.setAttribute('data-active', sortIsAsc ? '1' : '0');

    if (availableCheck) {
      availableCheck.checked = availActive;
      if (availableLabel) availableLabel.classList.toggle('is-checked', availActive);
    }

    if (searchInput) searchInput.value = searchQuery;
    updateShowcaseVisibility(searchQuery !== '');
    renderFilterOutput(params);
  }

  /* ─── イベントバインド ─────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-filter-group]');
    if (!btn) return;
    var group = btn.getAttribute('data-filter-group');
    var value = decodeURIComponent(btn.getAttribute('data-filter-value') || '');
    var url;
    if (group === 'product')     url = buildProductCatUrl(value);
    else if (group === 'udob')   url = buildUdoBCatUrl(value);
    if (url) navigateTo(url);
  });

  document.addEventListener('change', function (e) {
    if (e.target.id !== 'udob-available-only') return;
    navigateTo(buildAvailableUrl(e.target.checked));
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.udob-items__sort-btn');
    if (!btn) return;
    var sort = btn.getAttribute('data-sort');
    if (sort) navigateTo(buildSortUrl(sort));
  });

  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-facet-link]');
    if (!link) return;
    e.preventDefault();
    var href = link.href;
    /* sort_by がリンクURLに含まれていない場合、現在のURLから引き継ぐ */
    var linkParams = new URLSearchParams(href.split('?')[1] || '');
    if (!linkParams.has(PARAM_SORT)) {
      var currentSort = new URLSearchParams(window.location.search).get(PARAM_SORT);
      if (currentSort) {
        linkParams.set(PARAM_SORT, currentSort);
        href = COLLECTION_URL + '?' + linkParams.toString();
      }
    }
    navigateTo(href);
  });

  /* フリーワード検索（デバウンス 400ms） */
  var _searchTimer = null;
  if (searchInput) {
    searchInput.addEventListener('input', function () {
      clearTimeout(_searchTimer);
      var query = searchInput.value.trim();
      _searchTimer = setTimeout(function () {
        applyClientSearch(query);
      }, 400);
    });
  }

  /* ブラウザ戻る / 進む */
  window.addEventListener('popstate', function () {
    var params = new URLSearchParams(window.location.search);
    var q = params.get(PARAM_Q) || '';
    if (q) {
      if (searchInput) searchInput.value = q;
      applyClientSearch(q);
    } else {
      if (searchInput) searchInput.value = '';
      navigateTo(window.location.href, false);
    }
  });

  /* ─── 初期化 ──────────────────────────────────────────────────────── */
  var _initParams = new URLSearchParams(window.location.search);
  var _initQ      = _initParams.get(PARAM_Q) || '';

  if (_initQ) {
    if (searchInput) searchInput.value = _initQ;
    applyClientSearch(_initQ);
  } else if (!_initParams.has(PARAM_SORT)) {
    /* sort_by 未指定の初期表示 → 新着順でコンテンツを差し替え（スクロールなし） */
    navigateTo(buildSortUrl('created-descending'), false, true);
  } else {
    updateBorderRight();
    syncButtonStatesFromUrl(_initParams);
    initFadeObserver();
  }
  // タグ高さ均一化：defer実行のためDOMは確定済み。
  // ダブルrAFでブラウザの1描画サイクル完了後に計測、フォント後も再実行。
  requestAnimationFrame(function () {
    requestAnimationFrame(equalizeCardTags);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(equalizeCardTags);
      });
    });
  }
  setTimeout(equalizeCardTags, 500);
  window.addEventListener('resize', equalizeCardTags);
  window.addEventListener('resize', updateBorderRight);

})();
